import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type EffortLevel,
  resolveManagedChatModel,
} from "../../../shared/aiProviders";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { CLIENT_AUTO_RESOLVED_TOOLS } from "../../../shared/clientAutoResolvedTools";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import useChatScroll from "../../hooks/useChatScroll";
import { useChatSounds } from "../../hooks/useChatSounds";
import { useCreateSkill } from "../../hooks/useCreateSkill";
import { useFavicon } from "../../hooks/useFavicon";
import { useFileDropzone } from "../../hooks/useFileDropzone";
import { useModelExperiment } from "../../hooks/useModelExperiment";
import { type ModelScope, useScopedModel } from "../../hooks/useScopedModel";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePlanModeShortcut } from "../../hooks/usePlanModeShortcut";
import { useQuery } from "../../hooks/useQuery";
import {
  type MentionConnector,
  type MentionConnectorCatalogEntry,
  type MentionStep,
  useMentions,
} from "../../hooks/useMentions";
import { useSlashSkills } from "../../hooks/useSlashSkills";
import {
  type StreamMessage,
  useStreamingText,
} from "../../hooks/useStreamingText";
import { cacheToolOutput } from "../../hooks/useToolOutput";
import {
  type AttachmentPayload,
  toAttachmentPayload,
} from "../../lib/attachments";
import { maybeDisplayBrowserNotification } from "../../lib/browserNotifications";
import { getStatusState, type StatusState } from "../../lib/chatStatus";
import { useCaretToEndOnMount, useDraft } from "../../lib/draft";
import {
  loadEffort,
  resolveEffortFromEntries,
  saveEffort,
} from "../../lib/effort";
import { formatElapsedTime } from "../../lib/formatElapsedTime";
import {
  type BashFileTarget,
  inputToFileEdit,
  inputToFileTarget,
  inputToFileWrite,
  type StreamingBashWrite,
  type StreamingFileEdit,
} from "../../lib/parseStreamingBashWrite";
import {
  loadPlanMode,
  resolveDefaultPlanMode,
  resolvePlanModeFromEntries,
  resolveSeededPlanMode,
  savePlanMode,
} from "../../lib/planMode";
import { playChatNeedsInput, playClick } from "../../lib/sounds";
import { AttachmentHoverPreview } from "../AttachmentHoverPreview";
import {
  AttachmentPreview,
  AttachmentPreviewRow,
  AttachmentRowAnimator,
  attachmentFileIcon,
  Dropzone,
  marchingDashes,
} from "../AttachmentPreviews";
import { Button } from "../Button";
import { FlyingDot } from "../FlyingDot";
import { Icon } from "../Icon";
import { PlanModeToggle } from "../PlanModeToggle";
import { Spinner } from "../Spinner";
import { StatusIndicator } from "../StatusIndicator";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { Tooltip } from "../Tooltip";
import { VoiceRecorder } from "../VoiceRecorder";
import type { ChatEntryData, CodeSnippet, ElementSnippet } from "../Workflow";
import { ChatMessageNav } from "./ChatMessageNav";
import { type ChatTool, resolvePromptResult } from "./ChatTool";
import { ComposerStrip } from "./ComposerStrip";
import { ComposerStripToggle } from "./ComposerStripToggle";
import { FollowNudge } from "./FollowNudge";
import { ConnectorLogo } from "../ConnectorLogo";
import { AccentBar } from "./MessageContent";
import { SlashMenu, type SuggestionSet } from "./SlashMenu";
import { MentionCompletion } from "./MentionCompletion";
import { SlashSkillCompletion } from "./SlashSkillCompletion";
import { QueueStack } from "./QueueStack";
import { claimQueued, type QueuedMessage } from "./queueStorage";
import { useChatQueue } from "./useChatQueue";
import { navigate, routes } from "../../lib/urls";
import { prependAttachedConnectors } from "../../../shared/connectors/attachedConnectors";
import { prependAttachedSkills } from "../../../shared/skills/attachedSkills";
import { prependAttachedSteps } from "../../../shared/steps/attachedSteps";

import { type GroupedEntry, Transcript } from "./Transcript";

import {
  activeStates,
  StatusLabel,
  StatusTimer,
  statusText,
} from "./ChatStatus";
import {
  ChatForm,
  ComposerControlGroup,
  ComposerControls,
  ComposerInputWrap,
  Container,
  FollowAvatarButton,
  Messages,
  NextCheckpointLink,
  PrevCheckpointLink,
  RecordingDot,
  RecordingIndicator,
  ScrollToBottomButton,
  StatusContainer,
  StopButton,
  TextareaContainer,
  VoiceRecorderContainer,
} from "./chatStyles";

export type ChatSendPayload = {
  text: string;
  isDictated?: boolean;
  planMode?: boolean;
  effort?: EffortLevel;
  chatModel?: string;
  actionKind?:
    | "readme"
    | "error"
    | "connector"
    | "investigate_step_test"
    | "step_test_fixture_setup";
  attachments?: AttachmentPayload[];
  selectedStep?: string;
  interruptEntryId?: string;
};

export type ChatHandle = {
  enqueueOrSend: (payload: ChatSendPayload) => void;
};

export type ChatProps = {
  entries: readonly ChatEntryData[];
  writeChatId: string | null;
  draftKey: string;
  onSend: (payload: ChatSendPayload, onError: () => void) => void;
  onInterrupt: () => void;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean; chatModel?: string }
  ) => Promise<boolean>;
  prevCheckpointLink?: string;
  prevCheckpointLabel?: string;
  nextCheckpointLink?: string;
  nextCheckpointLabel?: string;
  isCurrentUserChat: boolean;
  canWrite: boolean;
  tools: readonly ChatTool[];
  suggestions: SuggestionSet;
  soundsEnabled: boolean;
  respondingAt: number | null;
  lastLoopHeartbeatAt?: number | null;
  awaitingAction: boolean;
  codeSnippets?: readonly CodeSnippet[];
  removeCodeSnippet?: (id: string) => void;
  clearCodeSnippets?: () => void;
  elementSnippets?: readonly ElementSnippet[];
  removeElementSnippet?: (id: string) => void;
  clearElementSnippets?: () => void;
  selectedStepName?: string;
  spaceId?: string;
  mentionSteps?: readonly MentionStep[];
  mentionConnectors?: readonly MentionConnector[];
  mentionTenantConnectors?: readonly MentionConnector[];
  mentionConnectorCatalog?: readonly MentionConnectorCatalogEntry[];
  mentionConnectorContextLabel?: string;
  historyBanner?: React.ReactNode;
  /**
   * Floats over the top of the message viewport without displacing the feed —
   * messages scroll underneath it. The node positions itself.
   */
  topOverlay?: React.ReactNode;
  /**
   * When set, replaces the chat’s tool-pending prompt with this node.
   * Used by the apps-CLI flow to inline a connect-app picker without
   * requiring an `AssistantToolUse` chat entry.
   */
  pendingPromptOverride?: React.ReactNode;
  outerScroll?: boolean;
  outerScrollRef?: React.RefObject<HTMLDivElement | null>;
  onStatusClick?: () => void;
  followActive?: boolean;
  showFollowNudge?: boolean;
  onDismissFollowNudge?: () => void;
  onStreamingToolStep?: (step: string | undefined) => void;
  onStatusChange?: (status: StatusState) => void;
  onStreamingFileWrite?: (write: StreamingBashWrite | null) => void;
  onStreamingFileEdit?: (edit: StreamingFileEdit | null) => void;
  onStreamingFileTarget?: (target: BashFileTarget | null) => void;
  planModeEnabled?: boolean;
  planModeDefaultSurface?: "chat" | "workflow" | "branch" | "none";
  resolveFileHref?: (href: string) => string | null;
  emptyState?: (api: {
    fillInput: (text: string) => void;
    visible: boolean;
  }) => React.ReactNode;
  // Lead phrase for the empty composer; the “/ for skills and prompts” hint is
  // appended when a slash menu is available.
  placeholder?: string;
  modelScope: ModelScope;
  onZipFile?: (file: File) => void;
};

function notifyChatDone(messages: ReadonlyMap<string, StreamMessage>): void {
  const text = [...messages.values()].findLast(
    (m) => m.role === "assistant" && m.text
  )?.text;
  if (!text) return;
  maybeDisplayBrowserNotification(
    "3B",
    text.replace(/\s+/g, " ").trim().slice(0, 200)
  );
}

export const Chat = forwardRef<ChatHandle, ChatProps>(function Chat(
  {
    entries,
    writeChatId,
    draftKey,
    onSend,
    onInterrupt,
    onProvideToolResult,
    prevCheckpointLink,
    prevCheckpointLabel,
    nextCheckpointLink,
    nextCheckpointLabel,
    isCurrentUserChat,
    canWrite,
    tools,
    suggestions,
    soundsEnabled,
    respondingAt,
    lastLoopHeartbeatAt,
    awaitingAction,
    codeSnippets,
    removeCodeSnippet,
    clearCodeSnippets,
    elementSnippets,
    removeElementSnippet,
    clearElementSnippets,
    selectedStepName,
    spaceId,
    mentionSteps,
    mentionConnectors,
    mentionTenantConnectors,
    mentionConnectorCatalog,
    mentionConnectorContextLabel,
    historyBanner,
    topOverlay,
    pendingPromptOverride,
    outerScroll = false,
    outerScrollRef,
    onStatusClick,
    followActive = false,
    showFollowNudge = false,
    onDismissFollowNudge,
    onStreamingToolStep,
    onStatusChange,
    onStreamingFileWrite,
    onStreamingFileEdit,
    onStreamingFileTarget,
    planModeEnabled = true,
    planModeDefaultSurface = "chat",
    resolveFileHref,
    emptyState,
    placeholder = "Ask AI to build",
    modelScope,
    onZipFile,
  },
  forwardedRef
) {
  const isResponding = respondingAt != null;
  const serverHasStartedRun = lastLoopHeartbeatAt != null;
  const { messages: streamingEntries, runStatus } = useStreamingText(
    writeChatId,
    isResponding && serverHasStartedRun,
    notifyChatDone
  );

  const streamingToolStep = useMemo(() => {
    let latest: string | undefined;
    for (const m of streamingEntries.values()) {
      if (m.role !== "tool") continue;
      const step = (m.toolInput as { step?: string } | undefined)?.step;
      if (step) latest = step;
    }
    return latest;
  }, [streamingEntries]);
  useEffect(() => {
    onStreamingToolStep?.(streamingToolStep);
  }, [streamingToolStep, onStreamingToolStep]);

  // Only the latest tool entry's input matters — older entries linger in the
  // streaming map and would otherwise shadow a newer tool.
  const lastStreamingToolInput = useMemo(() => {
    let latest: Record<string, unknown> | null = null;
    for (const m of streamingEntries.values()) {
      if (m.role !== "tool") continue;
      latest = (m.toolInput as Record<string, unknown> | undefined) ?? null;
    }
    return latest;
  }, [streamingEntries]);

  const streamingFileWrite = useMemo<StreamingBashWrite | null>(
    () => inputToFileWrite(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileWrite?.(streamingFileWrite);
  }, [streamingFileWrite, onStreamingFileWrite]);

  const streamingFileEdit = useMemo<StreamingFileEdit | null>(
    () => inputToFileEdit(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileEdit?.(streamingFileEdit);
  }, [streamingFileEdit, onStreamingFileEdit]);

  const streamingFileTarget = useMemo<BashFileTarget | null>(
    () => inputToFileTarget(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileTarget?.(streamingFileTarget);
  }, [streamingFileTarget, onStreamingFileTarget]);

  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const resolveForce = (modifier: boolean) =>
    followUpBehavior === "steer" ? !modifier : modifier;
  const defaultPlanMode =
    !planModeEnabled || planModeDefaultSurface === "none"
      ? false
      : resolveDefaultPlanMode(preferences, planModeDefaultSurface);
  const [input, setInput] = useDraft(draftKey);
  const historyPlanMode = useMemo(
    () => resolvePlanModeFromEntries(entries),
    [entries]
  );
  const hasPlanModeHistory = useMemo(
    () =>
      entries.some(
        (entry) =>
          entry.entryType === "UserMessage" &&
          entry.userMessage?.planMode != null
      ),
    [entries]
  );
  const [planMode, setPlanMode] = useState(
    () =>
      planModeEnabled && (historyPlanMode || loadPlanMode(writeChatId) === true)
  );
  const planModeChatIdRef = useRef<string | null>(writeChatId);
  const planModeTouchedRef = useRef(false);
  useEffect(() => {
    const chatChanged = planModeChatIdRef.current !== writeChatId;
    if (chatChanged) planModeTouchedRef.current = false;
    planModeChatIdRef.current = writeChatId;
    if (chatChanged || !planModeTouchedRef.current) {
      setPlanMode(
        planModeEnabled &&
          resolveSeededPlanMode({
            hasHistory: hasPlanModeHistory,
            historyPlanMode,
            savedPlanMode: loadPlanMode(writeChatId),
            defaultPlanMode,
            surface: planModeDefaultSurface,
          })
      );
    }
  }, [
    writeChatId,
    historyPlanMode,
    hasPlanModeHistory,
    defaultPlanMode,
    planModeEnabled,
    planModeDefaultSurface,
  ]);
  const togglePlanMode = useCallback(() => {
    if (!planModeEnabled) return;
    planModeTouchedRef.current = true;
    setPlanMode((on) => {
      const next = !on;
      savePlanMode(writeChatId, next);
      return next;
    });
  }, [planModeEnabled, writeChatId]);
  usePlanModeShortcut(togglePlanMode, planModeEnabled);
  const historyEffort = useMemo(
    () => resolveEffortFromEntries(entries),
    [entries]
  );
  const [localEffort, setLocalEffort] = useState<EffortLevel | null>(() =>
    loadEffort(writeChatId)
  );
  const [effortChatId, setEffortChatId] = useState(writeChatId);
  if (effortChatId !== writeChatId) {
    setEffortChatId(writeChatId);
    setLocalEffort(loadEffort(writeChatId));
  }
  const effort = localEffort ?? historyEffort;
  const [chatModel, setChatModel] = useState<string | null>(null);
  // A workflow conversation spans a chat per commit, so the branch id is the
  // stable thread key; a personal chat is a single chat.
  const experimentModel = useModelExperiment(
    modelScope.type === "branch" ? modelScope.branchId : writeChatId
  );
  const effectiveChatModel =
    chatModel ?? preferences?.chatModel ?? experimentModel ?? null;

  const composerDefaultsRef = useRef({ planMode, effort, effectiveChatModel });
  composerDefaultsRef.current = { planMode, effort, effectiveChatModel };

  const handleChatModelChange = (next: string | null) => {
    if (experimentModel && writeChatId) {
      const from = resolveManagedChatModel(effectiveChatModel);
      const to = next == null ? null : resolveManagedChatModel(next);
      const started = entries.some((e) => e.entryType === "UserMessage");
      if (started && to !== from) {
        if (to) {
          z.mutate(
            sharedMutators.recordModelExperimentEvent({
              chatId: writeChatId,
              kind: "switch",
              model: to,
              fromModel: from,
            })
          );
        }
      }
    }
    setChatModel(next);
  };
  const [selectedQueueItem, setSelectedQueueItem] = useState<{
    index: number;
    button: "cancel" | "edit" | "send";
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [celebrateTrigger, setCelebrateTrigger] = useState<number | null>(null);
  const celebrateCountRef = useRef(0);
  useEffect(() => {
    const count =
      input.match(
        /\b(party|finally|magic|wow|genius|beautiful|actually\s+works)\b/gi
      )?.length ?? 0;
    if (count > celebrateCountRef.current) {
      setCelebrateTrigger(Date.now());
    }
    celebrateCountRef.current = count;
  }, [input]);

  const isGroupChat = useMemo(() => {
    const authorIds = new Set<string>();
    for (const entry of entries) {
      const authorId =
        entry.userMessage?.user?.id ?? entry.userMessage?.serviceAccount?.id;
      if (authorId) authorIds.add(authorId);
      if (authorIds.size > 1) return true;
    }
    return false;
  }, [entries]);

  const { providerId: modelProviderId } = useScopedModel(modelScope, {
    chatModel: effectiveChatModel,
  });
  const {
    pendingAttachments,
    isUploading,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  } = useFileDropzone({
    enabled: isCurrentUserChat && canWrite,
    providerId: modelProviderId,
    onZipFile,
  });

  useEffect(() => {
    if (isDragging && soundsEnabled) playChatNeedsInput();
  }, [isDragging, soundsEnabled]);

  const statusState = getStatusState(
    entries as ChatEntryWithSubTables[],
    isResponding,
    runStatus,
    awaitingAction
  );
  const isActiveStatus = activeStates.includes(statusState);

  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);
  useChatSounds(statusState, isTypewriterAnimating, soundsEnabled);
  useFavicon(
    statusState,
    isTypewriterAnimating,
    modelScope.type === "personal" ? "chat" : "workflow"
  );
  useEffect(() => {
    onStatusChange?.(statusState);
  }, [statusState, onStatusChange]);

  const [scrollContainer, resetScroll, isScrolledUp] = useChatScroll(
    outerScroll ? outerScrollRef : undefined
  );

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);
  const [queueOverlayHeight, setQueueOverlayHeight] = useState(0);
  const queueOverlayObserverRef = useRef<ResizeObserver | null>(null);
  const setQueueOverlayRef = useCallback((el: HTMLDivElement | null) => {
    queueOverlayObserverRef.current?.disconnect();
    queueOverlayObserverRef.current = null;
    if (!el) {
      setQueueOverlayHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setQueueOverlayHeight(entry?.contentRect.height ?? 0);
    });
    observer.observe(el);
    queueOverlayObserverRef.current = observer;
  }, []);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const [topVisible, setTopVisible] = useState(false);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollContainer.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const [bottomVisible, setBottomVisible] = useState(false);
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    const root = scrollContainer.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setBottomVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainer]);

  const handleTypewriterAnimatingChange = useCallback((animating: boolean) => {
    setIsTypewriterAnimating(animating);
  }, []);
  const isMobile = useIsMobile();
  const textareaRef = useFocusShortcut<HTMLTextAreaElement>();
  useCaretToEndOnMount(textareaRef);
  const [isTextareaFocused, setIsTextareaFocused] = useState(true);
  const [showComposerFade, setShowComposerFade] = useState(false);
  const updateComposerFade = useCallback(() => {
    const el = textareaRef.current;
    setShowComposerFade(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [textareaRef]);
  const scrollComposerToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    updateComposerFade();
  }, [textareaRef, updateComposerFade]);

  const fillAnimationRef = useRef<number | null>(null);
  const fillInput = useCallback(
    (text: string) => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
      // Type into the composer at the same cadence as the space-list animated
      // placeholder, so a picked prompt animates in rather than snapping in.
      const charsPerSecond = 188;
      let startTime: number | null = null;
      const step = (time: number) => {
        if (startTime === null) startTime = time;
        const revealed = Math.min(
          text.length,
          Math.floor(((time - startTime) / 1000) * charsPerSecond)
        );
        setInput(text.slice(0, revealed));
        const el = textareaRef.current;
        if (el) {
          // Re-focus each frame: the picked card takes focus on pointerdown and
          // the empty state unmounts mid-animation, so the composer would
          // otherwise not be focused when the typing finishes.
          el.focus({ preventScroll: true });
          el.setSelectionRange(revealed, revealed);
        }
        scrollComposerToEnd();
        fillAnimationRef.current =
          revealed < text.length ? requestAnimationFrame(step) : null;
      };
      fillAnimationRef.current = requestAnimationFrame(step);
    },
    [textareaRef, scrollComposerToEnd, setInput]
  );

  useEffect(() => {
    return () => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(updateComposerFade);
  }, [updateComposerFade]);

  const snippetCount =
    (codeSnippets?.length ?? 0) + (elementSnippets?.length ?? 0);
  const prevSnippetCount = useRef(snippetCount);
  useEffect(() => {
    if (snippetCount > prevSnippetCount.current && !isMobile) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    prevSnippetCount.current = snippetCount;
  }, [snippetCount, isMobile, textareaRef]);

  // Focus the composer when a fresh chat opens (e.g. the "+" new-chat button),
  // deferred so it wins the focus race against the button that was just pressed.
  const didFocusNewChat = useRef(false);
  useEffect(() => {
    if (didFocusNewChat.current) return;
    if (isMobile || !canWrite || entries.length > 0) return;
    didFocusNewChat.current = true;
    const raf = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isMobile, canWrite, entries.length, textareaRef]);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0);
  const [expandedToolUse, setExpandedToolUse] = useState<string | null>(null);
  const [expandedToolGroups, setExpandedToolGroups] = useState<Set<string>>(
    new Set()
  );
  const [expandedUserActionGroups, setExpandedUserActionGroups] = useState<
    Set<string>
  >(new Set());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { canCreate: canCreateSkill, createSkill } = useCreateSkill(spaceId);

  const {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashCompletion,
    handleSlashKeyDown,
  } = useSlashSkills({
    spaceId,
    textareaRef,
    value: input,
    setValue: setInput,
    onAfterAccept: scrollComposerToEnd,
  });

  const {
    selectedSteps,
    removeStep,
    clearSteps,
    selectedConnectors,
    removeConnector,
    clearConnectors,
    syncMentionToken,
    mentionCompletion,
    handleMentionKeyDown,
  } = useMentions({
    textareaRef,
    value: input,
    setValue: setInput,
    steps: mentionSteps ?? [],
    connectors: mentionConnectors ?? [],
    tenantConnectors: mentionTenantConnectors ?? [],
    connectorCatalog: mentionConnectorCatalog ?? [],
    contextLabel: mentionConnectorContextLabel,
    onAfterAccept: scrollComposerToEnd,
  });

  const toolsByName = useMemo(
    () => new Map(tools.map((t) => [t.name, t])),
    [tools]
  );

  const scrollToBottom = useCallback(() => {
    const container = scrollContainer.current;
    if (!container) return;
    const distance =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distance < 4) {
      resetScroll();
      return;
    }
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    // no scrollend on iOS Safari; useChatScroll's scroll listener resets there
    container.addEventListener("scrollend", resetScroll, { once: true });
  }, [scrollContainer, resetScroll]);

  useEffect(() => {
    if (!recordingStartTime) return;
    const interval = setInterval(
      () => setRecordingElapsedTime(Date.now() - recordingStartTime),
      1000
    );
    return () => clearInterval(interval);
  }, [recordingStartTime]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  useEffect(resetScroll, []);

  // Group consecutive tool use entries when AI is done (resting)
  const groupedEntries = useMemo((): GroupedEntry[] => {
    const allEntries = entries as ChatEntryWithSubTables[];
    const filtered = allEntries.filter(
      (e, i) =>
        e.entryType !== "UserInterrupt" &&
        !(
          e.entryType === "AssistantReasoning" &&
          !e.assistantReasoning?.signature &&
          !(i === allEntries.length - 1 && statusState !== "resting")
        )
    );

    const result: GroupedEntry[] = [];
    let currentToolGroup: ChatEntryWithSubTables[] = [];
    let currentUserActionGroup: ChatEntryWithSubTables[] = [];
    let pendingReasoning: ChatEntryWithSubTables | undefined;

    const flushUserActionGroup = () => {
      const first = currentUserActionGroup[0];
      if (first) {
        result.push({
          type: "userActionGroup",
          entries: currentUserActionGroup,
          groupId: first.id,
        });
        currentUserActionGroup = [];
      }
    };

    const flushToolGroup = () => {
      const firstTool = currentToolGroup[0];
      if (firstTool) {
        result.push({
          type: "toolGroup",
          entries: currentToolGroup,
          groupId: firstTool.id,
          reasoning: pendingReasoning,
        });
        currentToolGroup = [];
        pendingReasoning = undefined;
      }
    };

    for (const entry of filtered) {
      if (
        entry.entryType === "UserAction" &&
        entry.userAction?.actionType !== "pushDraftToLive"
      ) {
        flushToolGroup();
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        currentUserActionGroup.push(entry);
      } else if (
        entry.entryType === "AssistantToolUse" &&
        entry.toolCall?.toolName === "approvePlan"
      ) {
        flushUserActionGroup();
        flushToolGroup();
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        result.push({ type: "single", entry });
      } else if (entry.entryType === "AssistantToolUse" && entry.toolCall) {
        flushUserActionGroup();
        currentToolGroup.push(entry);
      } else if (entry.entryType === "AssistantReasoning") {
        // Flush any accumulated groups before storing new reasoning
        flushUserActionGroup();
        flushToolGroup();
        pendingReasoning = entry;
      } else {
        // Flush any accumulated groups
        flushUserActionGroup();
        flushToolGroup();
        // If we have pending reasoning without tools, render it in a tool group
        if (pendingReasoning) {
          result.push({
            type: "toolGroup",
            entries: [],
            groupId: pendingReasoning.id,
            reasoning: pendingReasoning,
          });
          pendingReasoning = undefined;
        }
        result.push({ type: "single", entry });
      }
    }

    // Handle any remaining user action group at the end
    flushUserActionGroup();

    // Handle any remaining tool group at the end
    const remainingFirst = currentToolGroup[0];
    if (remainingFirst) {
      // Always group tools, but mark as trailing if AI is still working
      // This allows the collapse animation to play when AI finishes
      result.push({
        type: "toolGroup",
        entries: currentToolGroup,
        groupId: remainingFirst.id,
        reasoning: pendingReasoning,
        isTrailing: statusState !== "resting",
      });
    } else if (pendingReasoning) {
      // No tools followed the reasoning, render it in a tool group
      result.push({
        type: "toolGroup",
        entries: [],
        groupId: pendingReasoning.id,
        reasoning: pendingReasoning,
      });
    }

    return result;
  }, [entries, statusState]);

  // Clear expanded state when AI finishes (so new groups appear collapsed)
  useEffect(() => {
    if (statusState === "resting") {
      setExpandedToolGroups(new Set());
    }
  }, [statusState]);

  const withSelectedChatModel = useCallback(
    (payload: ChatSendPayload): ChatSendPayload => ({
      ...payload,
      chatModel:
        payload.chatModel ??
        composerDefaultsRef.current.effectiveChatModel ??
        undefined,
    }),
    []
  );

  const send = useCallback(
    (payload: ChatSendPayload, restoreOnError?: string) => {
      onSend(withSelectedChatModel(payload), () => {
        if (restoreOnError) setInput((prev) => prev || restoreOnError);
        requestAnimationFrame(scrollComposerToEnd);
      });
      resetScroll();
    },
    [onSend, resetScroll, scrollComposerToEnd, setInput, withSelectedChatModel]
  );

  const releaseQueued = useCallback(
    (card: QueuedMessage, markFailed: () => void) => {
      const { id: _id, state: _s, ...payload } = card;
      playClick();
      onSend(withSelectedChatModel(payload), markFailed);
      resetScroll();
    },
    [onSend, withSelectedChatModel, resetScroll]
  );
  const [queue, setQueue] = useChatQueue(
    writeChatId,
    isActiveStatus,
    editingId != null,
    releaseQueued
  );
  useEffect(() => {
    if (selectedQueueItem == null) return;
    if (input !== "") setSelectedQueueItem(null);
    else if (selectedQueueItem.index >= queue.length)
      setSelectedQueueItem(null);
  }, [input, queue.length, selectedQueueItem]);
  useEffect(() => {
    if (editingId != null && !queue.some((x) => x.id === editingId))
      setEditingId(null);
  }, [editingId, queue]);

  const enqueueOrSend = useCallback(
    (payload: ChatSendPayload, restoreOnError?: string) => {
      if (isActiveStatus) {
        setQueue((q) => [
          ...q,
          { id: nanoid(), ...withSelectedChatModel(payload) },
        ]);
      } else {
        send(payload, restoreOnError);
      }
    },
    [isActiveStatus, setQueue, withSelectedChatModel, send]
  );

  useImperativeHandle(forwardedRef, () => ({
    enqueueOrSend,
  }));

  const forceSend = (card: QueuedMessage) => {
    setQueue((q) => q.filter((x) => x.id !== card.id));
    if (writeChatId && !claimQueued(writeChatId, card.id)) return;
    const lastEntry = entries[entries.length - 1];
    const canInterrupt =
      lastEntry &&
      lastEntry.entryType !== "UserInterrupt" &&
      lastEntry.entryType !== "SystemMessage";
    const { id: _id, state: _s, ...payload } = card;
    send(
      {
        ...payload,
        ...(canInterrupt && { interruptEntryId: nanoid(12) }),
      },
      payload.text
    );
  };

  const cancelQueued = (id: string) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const editQueued = (id: string, text: string) => {
    if (!text.trim()) return;
    setQueue((q) =>
      q.map((x) => (x.id === id ? { ...x, text, state: undefined } : x))
    );
  };

  const retryFromError = useCallback(
    (errorEntryId: string) => {
      const idx = entries.findIndex((e) => e.id === errorEntryId);
      const preceding = idx === -1 ? entries : entries.slice(0, idx);
      const lastUser = preceding.findLast((e) => e.entryType === "UserMessage");
      const text = lastUser?.userMessage?.text;
      if (!text) return;
      const { planMode, effort } = composerDefaultsRef.current;
      enqueueOrSend(
        {
          text,
          isDictated: !!lastUser?.userMessage?.isDictated,
          planMode,
          effort: effort ?? undefined,
        },
        text
      );
    },
    [entries, enqueueOrSend]
  );

  const hasStagedAttachments = pendingAttachments.length > 0;
  const canSendAttachments = hasStagedAttachments && !isUploading;

  const doSend = (force = false) => {
    if (isUploading) return;
    const hasText = input.trim().length > 0;
    const hasSnippets = (codeSnippets ?? []).length > 0;
    const hasElementSnippets = (elementSnippets ?? []).length > 0;
    const hasSkills = selectedSkills.length > 0;
    const hasSteps = selectedSteps.length > 0;
    const hasConnectors = selectedConnectors.length > 0;
    if (
      !hasText &&
      !hasStagedAttachments &&
      !hasSnippets &&
      !hasElementSnippets &&
      !hasSkills &&
      !hasSteps &&
      !hasConnectors
    )
      return;

    const attachments = toAttachmentPayload(pendingAttachments);

    let text = input.trim();

    if (hasSnippets) {
      const blocks = codeSnippets!.map((s) => {
        const lines =
          s.fromLine === s.toLine
            ? `${s.fromLine}`
            : `${s.fromLine}-${s.toLine}`;
        const displayPath = s.filePath.split("/").slice(1).join("/");
        const revisionAttr =
          s.revision === "previous" ? ` revision="previous"` : "";
        return `<code-context file="${displayPath}" lines="${lines}"${revisionAttr} color="${s.color}">\n${s.text}\n</code-context>`;
      });
      text = blocks.join("\n\n") + (text ? `\n\n${text}` : "");
    }

    if (hasElementSnippets) {
      const blocks = elementSnippets!.map(
        (s) =>
          `<element-context step="${s.stepName}" element="${s.descriptor}" color="${s.color}" index="${s.index}">\n<outer-html>\n${s.outerHtml}\n</outer-html>\n<text-content>\n${s.textContent}\n</text-content>\n</element-context>`
      );
      text = blocks.join("\n\n") + (text ? `\n\n${text}` : "");
    }

    text = prependAttachedConnectors(
      prependAttachedSteps(
        prependAttachedSkills(text, selectedSkills),
        selectedSteps
      ),
      selectedConnectors
    );

    const payload = {
      text,
      selectedStep: selectedStepName,
      planMode,
      effort: effort ?? undefined,
      attachments,
    };
    const draftBeforeClear = input;
    if (force && isActiveStatus) {
      const lastEntry = entries[entries.length - 1];
      const canInterrupt =
        lastEntry &&
        lastEntry.entryType !== "UserInterrupt" &&
        lastEntry.entryType !== "SystemMessage";
      send(
        {
          ...payload,
          ...(canInterrupt && { interruptEntryId: nanoid(12) }),
        },
        draftBeforeClear
      );
    } else {
      enqueueOrSend(payload, draftBeforeClear);
    }
    setInput("");
    requestAnimationFrame(updateComposerFade);
    clearAttachments();
    clearCodeSnippets?.();
    clearElementSnippets?.();
    clearSkills();
    clearSteps();
    clearConnectors();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSend(resolveForce(false));
  };

  const pendingPromptTool = useMemo(() => {
    if (statusState !== "waiting") return null;
    const lastTool = (entries as ChatEntryWithSubTables[]).findLast((entry) => {
      if (entry.entryType !== "AssistantToolUse") return false;
      const call = entry.toolCall;
      if (!call || call.completedAt != null) return false;
      return !!toolsByName.get(call.toolName)?.renderPendingPrompt;
    });
    if (!lastTool?.toolCall) return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.renderPendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      input: lastTool.toolCall.input,
      renderPendingPrompt: tool.renderPendingPrompt,
    };
  }, [entries, statusState, toolsByName]);

  const autoResolvedTool = useMemo(() => {
    if (isResponding) return null;
    const lastTool = (entries as ChatEntryWithSubTables[]).findLast((entry) => {
      if (entry.entryType !== "AssistantToolUse") return false;
      const call = entry.toolCall;
      if (!call || call.completedAt != null) return false;
      return (
        CLIENT_AUTO_RESOLVED_TOOLS.has(call.toolName) &&
        !!toolsByName.get(call.toolName)?.renderPendingPrompt
      );
    });
    if (!lastTool?.toolCall) return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.renderPendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      input: lastTool.toolCall.input,
      renderPendingPrompt: tool.renderPendingPrompt,
    };
  }, [entries, isResponding, toolsByName]);

  const toolResultsInFlight = useRef(new Map<string, Promise<boolean>>());
  const handleProvideToolResult = useCallback(
    (
      entryId: string,
      output: unknown,
      isError: boolean,
      options?: { pauseAfter?: boolean }
    ) => {
      if (!writeChatId) return Promise.resolve(false);
      const pending = toolResultsInFlight.current.get(entryId);
      if (pending) return pending;
      cacheToolOutput(entryId, output);
      const result = onProvideToolResult(entryId, output, isError, {
        ...options,
        chatModel: composerDefaultsRef.current.effectiveChatModel ?? undefined,
      });
      toolResultsInFlight.current.set(entryId, result);
      const clear = () => toolResultsInFlight.current.delete(entryId);
      void result.then(clear, clear);
      resetScroll();
      return result;
    },
    [writeChatId, onProvideToolResult, resetScroll]
  );

  const pendingPromptNode =
    pendingPromptTool?.renderPendingPrompt({
      toolCallId: pendingPromptTool.toolCallId,
      input: pendingPromptTool.input,
      hasStagedAttachments: canSendAttachments,
      onResult: (output, isError, options) => {
        if (!writeChatId) return Promise.resolve(false);
        const { approvedPlan, sendAttachments, resultOptions } =
          resolvePromptResult(options, canSendAttachments);
        if (approvedPlan) {
          planModeTouchedRef.current = false;
          savePlanMode(writeChatId, false);
          setPlanMode(false);
        }
        const result = handleProvideToolResult(
          pendingPromptTool.toolCallId,
          output,
          isError,
          resultOptions
        );
        if (sendAttachments) {
          send({
            text: "",
            attachments: toAttachmentPayload(pendingAttachments),
            selectedStep: selectedStepName,
            planMode: approvedPlan ? false : planMode,
            effort: effort ?? undefined,
          });
          clearAttachments();
        }
        return result;
      },
    }) ?? null;

  const autoResolvedToolNode =
    autoResolvedTool?.renderPendingPrompt({
      toolCallId: autoResolvedTool.toolCallId,
      input: autoResolvedTool.input,
      hasStagedAttachments: false,
      onResult: (output, isError, options) =>
        handleProvideToolResult(
          autoResolvedTool.toolCallId,
          output,
          isError,
          options
        ),
    }) ?? null;

  const handleSelectSuggestion = (prompt: string) => {
    send({ text: prompt, planMode, effort: effort ?? undefined }, prompt);
  };

  return (
    <Container data-outer-scroll={outerScroll || undefined}>
      <div
        ref={messagesWrapperRef}
        style={{ flex: 1, minHeight: 0, position: "relative" }}
      >
        <Messages
          ref={outerScroll ? undefined : scrollContainer}
          data-outer-scroll={outerScroll || undefined}
          onMouseDownCapture={(e) => {
            if (entries.length === 0) {
              e.preventDefault();
              textareaRef.current?.focus();
            }
          }}
          style={
            !isCurrentUserChat
              ? { paddingBottom: 16 }
              : queueOverlayHeight > 0
                ? { paddingBottom: 56 + queueOverlayHeight + 12 }
                : undefined
          }
        >
          <div
            ref={topSentinelRef}
            style={{ height: 0, overflow: "hidden", flexShrink: 0 }}
          />
          {prevCheckpointLink && prevCheckpointLabel && (
            <PrevCheckpointLink
              to={prevCheckpointLink}
              data-visible={topVisible}
            >
              <Icon name="rotate-ccw" size={10} color="var(--text-secondary)" />
              <Text variant="text-small" color="var(--text-secondary)">
                {prevCheckpointLabel}
              </Text>
            </PrevCheckpointLink>
          )}
          <Transcript
            groupedEntries={groupedEntries}
            isGroupChat={isGroupChat}
            currentUserId={z.userID}
            expandedToolGroups={expandedToolGroups}
            setExpandedToolGroups={setExpandedToolGroups}
            expandedUserActionGroups={expandedUserActionGroups}
            setExpandedUserActionGroups={setExpandedUserActionGroups}
            expandedToolUse={expandedToolUse}
            setExpandedToolUse={setExpandedToolUse}
            toolsByName={toolsByName}
            streamingEntries={streamingEntries}
            isCurrentUserChat={isCurrentUserChat}
            canWrite={canWrite}
            onProvideToolResult={handleProvideToolResult}
            onRetryFromError={retryFromError}
            onTypewriterAnimatingChange={handleTypewriterAnimatingChange}
            resolveFileHref={resolveFileHref}
          />
          {nextCheckpointLink && nextCheckpointLabel && (
            <>
              <div ref={bottomSentinelRef} />
              <NextCheckpointLink
                to={nextCheckpointLink}
                data-visible={bottomVisible}
              >
                <Icon
                  name="rotate-cw"
                  size={10}
                  color="var(--text-secondary)"
                />
                <Text variant="text-small" color="var(--text-secondary)">
                  {nextCheckpointLabel}
                </Text>
              </NextCheckpointLink>
            </>
          )}
        </Messages>
        {topOverlay}
        {isCurrentUserChat &&
          entries.length === 0 &&
          emptyState?.({ fillInput, visible: input === "" })}
        {!outerScroll && (
          <ChatMessageNav
            entries={entries as ChatEntryWithSubTables[]}
            scrollContainer={scrollContainer}
            wrapperRef={messagesWrapperRef}
          />
        )}
        {isCurrentUserChat && canWrite && (
          <QueueStack
            queue={queue}
            overlayRef={setQueueOverlayRef}
            onForceSend={forceSend}
            onCancel={cancelQueued}
            scrollContainer={scrollContainer}
            selectedIndex={selectedQueueItem?.index ?? null}
            selectedButton={selectedQueueItem?.button ?? null}
            editingId={editingId}
            onEditStart={(id) => setEditingId(id)}
            onEditCommit={(id, text) => {
              editQueued(id, text);
              setEditingId(null);
              textareaRef.current?.focus();
            }}
            onEditCancel={() => {
              setEditingId(null);
              textareaRef.current?.focus();
            }}
          />
        )}
      </div>

      {isCurrentUserChat && (
        <ChatForm
          onSubmit={handleSubmit}
          data-outer-scroll={outerScroll || undefined}
        >
          <TextareaContainer
            style={{
              width: "100%",
              ...{
                padding: 4,
                margin: -4.5,
                background:
                  "linear-gradient(var(--alpha2), var(--alpha2)) padding-box, light-dark(var(--surface-secondary), var(--surface-primary))",
                border: "0.5px solid var(--border-alpha)",
              },
            }}
          >
            {isScrolledUp && (
              <ScrollToBottomButton
                onPress={scrollToBottom}
                style={
                  queueOverlayHeight > 0
                    ? {
                        transform: `translate(-50%, calc(-100% - 12px - ${queueOverlayHeight + 12}px))`,
                      }
                    : undefined
                }
              >
                <Icon name="arrow-down" size={12} />
              </ScrollToBottomButton>
            )}
            <StatusContainer>
              {onStatusClick ? (
                <FollowNudge
                  show={showFollowNudge}
                  trigger={({ style }) => (
                    <span style={{ ...style, display: "inline-flex" }}>
                      <Tooltip
                        content={
                          followActive ? "Stop following 3B" : "Follow 3B"
                        }
                        hidden={showFollowNudge}
                      >
                        <FollowAvatarButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onDismissFollowNudge?.();
                            onStatusClick();
                          }}
                          aria-label={
                            followActive ? "Stop following 3B" : "Follow 3B"
                          }
                        >
                          <StatusIndicator
                            state={isDragging ? "dragging" : statusState}
                            focused={
                              isDragging || isTextareaFocused || isRecording
                            }
                            planMode={planMode}
                            celebrateTrigger={celebrateTrigger}
                          />
                        </FollowAvatarButton>
                      </Tooltip>
                    </span>
                  )}
                />
              ) : (
                <StatusIndicator
                  state={isDragging ? "dragging" : statusState}
                  focused={isDragging || isTextareaFocused || isRecording}
                  planMode={planMode}
                  celebrateTrigger={celebrateTrigger}
                />
              )}
              <Text
                variant="text-small-md"
                ellipsis
                color="var(--text-primary)"
                style={{
                  opacity:
                    isDragging ||
                    isRecording ||
                    isActiveStatus ||
                    isTextareaFocused ||
                    planMode
                      ? 1
                      : 0.2,
                }}
              >
                {isDragging ? (
                  statusText.dragging
                ) : isRecording ? (
                  "Listening"
                ) : (
                  <StatusLabel
                    statusState={statusState}
                    respondingAt={respondingAt}
                    lastLoopHeartbeatAt={lastLoopHeartbeatAt}
                    planMode={planMode}
                  />
                )}
              </Text>
              {isActiveStatus && !isRecording && respondingAt && (
                <StatusTimer respondingAt={respondingAt} />
              )}
              {isRecording && (
                <RecordingIndicator>
                  <RecordingDot />
                  <Text variant="text-small-md" color="var(--text-secondary)">
                    {formatElapsedTime(recordingElapsedTime)}
                  </Text>
                </RecordingIndicator>
              )}
            </StatusContainer>

            <AttachmentRowAnimator
              visible={
                pendingAttachments.length > 0 ||
                (codeSnippets ?? []).length > 0 ||
                (elementSnippets ?? []).length > 0 ||
                selectedSkills.length > 0 ||
                selectedSteps.length > 0 ||
                selectedConnectors.length > 0
              }
            >
              <AttachmentPreviewRow>
                {selectedSkills.map((skill) => (
                  <AttachmentPreview
                    key={skill.id}
                    onPress={() => removeSkill(skill.id)}
                  >
                    <span className="file-icon">
                      <Icon name="skills" size={10} />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {skill.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {selectedSteps.map((step) => (
                  <AttachmentPreview
                    key={step.name}
                    onPress={() => removeStep(step.name)}
                  >
                    <span className="file-icon">
                      <AccentBar
                        style={{ color: `var(--${step.color}-600)` }}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {step.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {selectedConnectors.map((connector) => (
                  <AttachmentPreview
                    key={connector.name}
                    onPress={() => removeConnector(connector.name)}
                  >
                    <span className="file-icon">
                      <ConnectorLogo
                        template={{
                          name: connector.name,
                          identifier: connector.identifier,
                        }}
                        size={10}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {connector.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {pendingAttachments.map((att) => (
                  <AttachmentHoverPreview
                    key={att.id}
                    fileName={att.fileName}
                    mediaType={att.mediaType}
                    sizeBytes={att.sizeBytes}
                    url={att.previewUrl}
                    onRemove={() => removeAttachment(att.id)}
                  >
                    <AttachmentPreview onPress={() => removeAttachment(att.id)}>
                      <span className="file-icon">
                        {att.remoteId ? (
                          <Icon
                            name={attachmentFileIcon(att.mediaType)}
                            size={10}
                          />
                        ) : (
                          <Spinner size={10} />
                        )}
                      </span>
                      <span className="remove-icon">
                        <Icon name="x" size={10} />
                      </span>
                      <Text variant="text-small-md" ellipsis>
                        {att.fileName}
                      </Text>
                    </AttachmentPreview>
                  </AttachmentHoverPreview>
                ))}
                {(codeSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeCodeSnippet?.(snippet.id)}
                  >
                    <span className="file-icon">
                      <AccentBar
                        style={{
                          color: `var(--${snippet.color}-600)`,
                        }}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {snippet.filePath.split("/").slice(1).join("/")}:{" "}
                      {snippet.fromLine === snippet.toLine
                        ? snippet.fromLine
                        : `${snippet.fromLine}–${snippet.toLine}`}
                    </Text>
                  </AttachmentPreview>
                ))}
                {(elementSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeElementSnippet?.(snippet.id)}
                    onMouseEnter={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: { index: snippet.index },
                        })
                      )
                    }
                    onMouseLeave={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: { index: null },
                        })
                      )
                    }
                  >
                    <span
                      className="file-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        color: "white",
                        fontSize: 9,
                        fontWeight: 500,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      {snippet.index}
                    </span>
                    <span
                      className="remove-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      <Icon name="x" size={10} color="white" />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {snippet.label || snippet.descriptor}
                    </Text>
                  </AttachmentPreview>
                ))}
              </AttachmentPreviewRow>
            </AttachmentRowAnimator>

            {autoResolvedToolNode}
            {pendingPromptOverride ? (
              pendingPromptOverride
            ) : pendingPromptNode ? (
              pendingPromptNode
            ) : showSuggestions ? (
              <SlashMenu
                onSelect={handleSelectSuggestion}
                onSelectSkill={(skill) => {
                  addSkill(skill);
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                onDismiss={() => setShowSuggestions(false)}
                suggestions={suggestions}
                skills={availableSkills}
                canCreateSkill={canCreateSkill}
                onCreateSkill={() => {
                  const id = createSkill();
                  if (id) navigate(routes.modal.skill, { id: id });
                }}
              />
            ) : isRecording ? (
              <VoiceRecorderContainer>
                <VoiceRecorder
                  leftControls={
                    planModeEnabled ? (
                      <PlanModeToggle
                        planMode={planMode}
                        onToggle={togglePlanMode}
                      />
                    ) : undefined
                  }
                  onTranscript={(text) => {
                    const trimmed = text.trim();
                    setInput(trimmed);
                    requestAnimationFrame(() => {
                      const el = textareaRef.current;
                      if (el) {
                        el.focus();
                        el.setSelectionRange(trimmed.length, trimmed.length);
                      }
                      scrollComposerToEnd();
                    });
                  }}
                  onCancel={() => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                  }}
                  onSend={(text) => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                    if (isUploading) {
                      setInput(text);
                      return;
                    }
                    if (!text && !hasStagedAttachments) return;
                    enqueueOrSend(
                      {
                        text,
                        isDictated: true,
                        planMode,
                        effort: effort ?? undefined,
                        attachments: toAttachmentPayload(pendingAttachments),
                      },
                      text
                    );
                    setInput("");
                    requestAnimationFrame(updateComposerFade);
                    clearAttachments();
                  }}
                />
              </VoiceRecorderContainer>
            ) : (
              <ComposerInputWrap
                data-composer
                data-show-fade={showComposerFade || undefined}
              >
                <Textarea
                  autoFocus={!isMobile}
                  disabled={!canWrite}
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    const shouldFollowCaret =
                      e.target.selectionStart === e.target.value.length &&
                      e.target.selectionEnd === e.target.value.length;
                    setInput(e.target.value);
                    syncSlashToken(e.target);
                    syncMentionToken(e.target);
                    requestAnimationFrame(
                      shouldFollowCaret
                        ? scrollComposerToEnd
                        : updateComposerFade
                    );
                  }}
                  onKeyUp={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onClick={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onSelect={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onScroll={updateComposerFade}
                  placeholder={
                    isDragging
                      ? ""
                      : suggestions.items.length > 0 ||
                          availableSkills.length > 0
                        ? `${placeholder}, or type / for skills and prompts…`
                        : `${placeholder}…`
                  }
                  onFocus={() => setIsTextareaFocused(true)}
                  onBlur={() => setIsTextareaFocused(false)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (handleSlashKeyDown(e)) return;
                    if (handleMentionKeyDown(e)) return;
                    if (
                      e.key === "/" &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      input === "" &&
                      (suggestions.items.length > 0 ||
                        availableSkills.length > 0)
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      return;
                    }
                    if (input === "" && queue.length > 0) {
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSelectedQueueItem((prev) => ({
                          index:
                            prev == null
                              ? queue.length - 1
                              : Math.max(0, prev.index - 1),
                          button: prev?.button ?? "send",
                        }));
                        return;
                      }
                      if (e.key === "ArrowDown" && selectedQueueItem != null) {
                        e.preventDefault();
                        if (selectedQueueItem.index >= queue.length - 1) {
                          setSelectedQueueItem(null);
                        } else {
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            index: selectedQueueItem.index + 1,
                          });
                        }
                        return;
                      }
                      if (selectedQueueItem != null) {
                        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                          e.preventDefault();
                          const order = ["cancel", "edit", "send"] as const;
                          const delta = e.key === "ArrowLeft" ? -1 : 1;
                          const idx = Math.min(
                            order.length - 1,
                            Math.max(
                              0,
                              order.indexOf(selectedQueueItem.button) + delta
                            )
                          );
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            button: order[idx]!,
                          });
                          return;
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const card = queue[selectedQueueItem.index];
                          if (card) {
                            playClick();
                            if (selectedQueueItem.button === "send")
                              forceSend(card);
                            else if (selectedQueueItem.button === "edit")
                              setEditingId(card.id);
                            else cancelQueued(card.id);
                          }
                          setSelectedQueueItem(null);
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedQueueItem(null);
                          return;
                        }
                      }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      doSend(resolveForce(e.metaKey || e.ctrlKey));
                    }
                    if (e.key === "Escape") {
                      e.currentTarget.blur();
                      e.stopPropagation();
                    }
                  }}
                  minRows={4}
                  maxRows={20}
                  style={{
                    minHeight: 96,
                    paddingRight: 12,
                    paddingBottom: 44,
                    ...(isDragging && { color: "transparent" }),
                    // While a completion shows, the overlay renders the text so the ghost can push what follows the caret; the textarea keeps only the caret.
                    ...((slashCompletion.suffix !== "" ||
                      mentionCompletion.suffix !== "") && {
                      color: "transparent",
                      caretColor: "var(--text-primary)",
                    }),
                  }}
                />
                <SlashSkillCompletion
                  textareaRef={textareaRef}
                  value={input}
                  isTextareaFocused={isTextareaFocused}
                  completion={slashCompletion}
                />
                <MentionCompletion
                  textareaRef={textareaRef}
                  value={input}
                  isTextareaFocused={isTextareaFocused}
                  completion={mentionCompletion}
                />
                {!isDragging && (
                  <ComposerControls>
                    {planModeEnabled && (
                      <ComposerControlGroup>
                        <PlanModeToggle
                          planMode={planMode}
                          onToggle={togglePlanMode}
                        />
                      </ComposerControlGroup>
                    )}
                    <ComposerStripToggle />
                    <ComposerControlGroup>
                      <Tooltip content="Voice mode">
                        <Button
                          type="button"
                          icon="mic"
                          variant="secondary"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setIsRecording(true);
                            setRecordingStartTime(Date.now());
                            setRecordingElapsedTime(0);
                          }}
                          disabled={!isCurrentUserChat}
                        />
                      </Tooltip>
                      {isActiveStatus && writeChatId && !input.trim() ? (
                        <Tooltip content="Stop">
                          <StopButton
                            type="button"
                            icon="stop"
                            variant="secondary"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const lastEntry = entries[entries.length - 1];
                              if (
                                lastEntry &&
                                lastEntry.entryType !== "UserInterrupt" &&
                                lastEntry.entryType !== "SystemMessage"
                              ) {
                                onInterrupt();
                              }
                            }}
                            disabled={!canWrite}
                          />
                        </Tooltip>
                      ) : (
                        <Button
                          icon="arrow-up"
                          type="button"
                          onClick={(e) =>
                            doSend(resolveForce(e.metaKey || e.ctrlKey))
                          }
                          disabled={
                            isUploading ||
                            (!input.trim() &&
                              pendingAttachments.length === 0 &&
                              (codeSnippets ?? []).length === 0 &&
                              (elementSnippets ?? []).length === 0)
                          }
                        />
                      )}
                    </ComposerControlGroup>
                  </ComposerControls>
                )}
                {isDragging && (
                  <Dropzone
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                  >
                    <svg
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="calc(100% - 2px)"
                        height="calc(100% - 2px)"
                        rx="12"
                        ry="12"
                        fill="none"
                        stroke="var(--text-primary)"
                        strokeWidth="1"
                        strokeDasharray="6 4"
                        style={{
                          animation: `${marchingDashes} 0.5s linear infinite`,
                        }}
                      />
                    </svg>
                    <Icon
                      name="arrow-down"
                      size={10}
                      color="var(--text-primary)"
                    />
                    <Text variant="text-small" color="var(--text-primary)">
                      Drop files here
                    </Text>
                  </Dropzone>
                )}
              </ComposerInputWrap>
            )}
            <ComposerStrip
              onAddFiles={processFiles}
              modelScope={modelScope}
              effort={effort}
              onEffortChange={(next) => {
                saveEffort(writeChatId, next);
                setLocalEffort(next);
              }}
              chatModel={effectiveChatModel}
              onChatModelChange={handleChatModelChange}
            />
          </TextareaContainer>
        </ChatForm>
      )}
      {!isCurrentUserChat && historyBanner && (
        <div style={{ padding: "0 4px 4px", margin: "-0.5px" }}>
          {historyBanner}
        </div>
      )}
      {flyingFiles.map((f) => (
        <FlyingDot
          key={f.id}
          startX={f.x}
          startY={f.y}
          onDone={() => removeFlyingFile(f.id)}
        />
      ))}
    </Container>
  );
});
